import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Dialog, Transition } from '@headlessui/react';
import { Fragment } from 'react';
import { XMarkIcon, SparklesIcon, PlusIcon, TrashIcon } from '@heroicons/react/24/outline';
import { supabase } from '../../lib/supabase';
import { toast } from 'react-hot-toast';
import { prospectActivateSchema } from '../../lib/validation';
import type { ProspectActivateData } from '../../lib/validation';
import { useSponsorshipTiers } from '../../hooks/useSponsorshipTiers';

interface ProspectContact {
  id: string;
  full_name: string;
  email?: string;
  phone?: string;
  is_primary: boolean;
}

interface ProspectCommitment {
  tier_interest?: string;
  amount_pledged?: number;
}

interface Prospect {
  id: string;
  company_name: string;
  industry?: string;
  website?: string;
  stage: string;
  source?: string;
  notes?: string;
  tournament_id?: string;
  logo_url?: string;
  prospect_contacts?: ProspectContact[];
  prospect_commitments?: ProspectCommitment[];
}

interface ProspectActivateModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  prospect: Prospect;
}

export default function ProspectActivateModal({ isOpen, onClose, onSuccess, prospect }: ProspectActivateModalProps) {
  const [benefits, setBenefits] = useState<string[]>([]);
  const [newBenefit, setNewBenefit] = useState('');
  const { tiers } = useSponsorshipTiers();

  const primaryContact = prospect.prospect_contacts?.find(c => c.is_primary) || prospect.prospect_contacts?.[0];
  const latestCommitment = prospect.prospect_commitments?.[0];

  const { register, handleSubmit, watch, setValue, formState: { errors, isSubmitting } } = useForm<ProspectActivateData>({
    resolver: zodResolver(prospectActivateSchema),
    defaultValues: {
      contact_person: primaryContact?.full_name || '',
      email: primaryContact?.email || '',
      phone: primaryContact?.phone || '',
      tier: latestCommitment?.tier_interest || '',
      contribution: latestCommitment?.amount_pledged || 0,
      start_date: new Date().toISOString().split('T')[0],
      end_date: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      benefits: [],
    },
  });

  const selectedTier = watch('tier');

  useEffect(() => {
    if (selectedTier) {
      const tier = tiers.find(t => t.name === selectedTier);
      if (tier) {
        setBenefits(tier.features || []);
        setValue('benefits', tier.features || []);
        if (!watch('contribution') && tier.min_contribution) {
          setValue('contribution', tier.min_contribution);
        }
      }
    }
  }, [selectedTier, tiers, setValue, watch]);

  const addBenefit = () => {
    if (!newBenefit.trim()) return;
    const updated = [...benefits, newBenefit.trim()];
    setBenefits(updated);
    setValue('benefits', updated);
    setNewBenefit('');
  };

  const removeBenefit = (idx: number) => {
    const updated = benefits.filter((_, i) => i !== idx);
    setBenefits(updated);
    setValue('benefits', updated);
  };

  const onSubmit = async (data: ProspectActivateData) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();

      const { data: newPartner, error: partnerError } = await supabase
        .from('partners')
        .insert([{
          company_name:   prospect.company_name,
          contact_person: data.contact_person,
          email:          data.email,
          phone:          data.phone,
          website:        prospect.website || null,
          tier:           data.tier,
          status:         'pending',
          contribution:   data.contribution,
          start_date:     data.start_date,
          end_date:       data.end_date,
          benefits:       data.benefits,
          description:    prospect.notes || null,
          tournament_id:  prospect.tournament_id || null,
          logo_url:       prospect.logo_url || null,
        }])
        .select()
        .single();

      if (partnerError) throw partnerError;

      const { error: updateError } = await supabase
        .from('prospects')
        .update({ stage: 'won', partner_id: newPartner.id })
        .eq('id', prospect.id);

      if (updateError) throw updateError;

      await supabase.from('prospect_activities').insert([
        {
          prospect_id:   prospect.id,
          activity_type: 'stage_change',
          title:         'Stage changed to Won',
          metadata:      { from_stage: prospect.stage, to_stage: 'won' },
          performed_by:  user?.id,
        },
        {
          prospect_id:   prospect.id,
          activity_type: 'prospect_activated',
          title:         `Activated as confirmed partner`,
          body:          data.notes || null,
          metadata:      { partner_id: newPartner.id, tier: data.tier, contribution: data.contribution },
          performed_by:  user?.id,
        },
      ]);

      await supabase.from('partner_status_history').insert([{
        partner_id:  newPartner.id,
        old_status:  null,
        new_status:  'pending',
        notes:       `Activated from prospect pipeline`,
      }]);

      toast.success(`${prospect.company_name} activated as a partner!`);
      onSuccess();
      onClose();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Activation failed');
    }
  };

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child as={Fragment} enter="ease-out duration-200" enterFrom="opacity-0" enterTo="opacity-100" leave="ease-in duration-150" leaveFrom="opacity-100" leaveTo="opacity-0">
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" />
        </Transition.Child>
        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child as={Fragment} enter="ease-out duration-200" enterFrom="opacity-0 scale-95" enterTo="opacity-100 scale-100" leave="ease-in duration-150" leaveFrom="opacity-100 scale-100" leaveTo="opacity-0 scale-95">
              <Dialog.Panel className="w-full max-w-2xl bg-white rounded-2xl shadow-xl overflow-hidden">
                <div className="bg-golf-gray px-6 py-4 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-golf-orange/20 flex items-center justify-center">
                      <SparklesIcon className="w-4 h-4 text-golf-orange" />
                    </div>
                    <div>
                      <Dialog.Title className="text-white font-semibold">Activate as Partner</Dialog.Title>
                      <p className="text-gray-400 text-xs">{prospect.company_name}</p>
                    </div>
                  </div>
                  <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-white/10 transition-colors">
                    <XMarkIcon className="w-5 h-5 text-gray-300" />
                  </button>
                </div>

                <form onSubmit={handleSubmit(onSubmit)}>
                  <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-4">
                      <div className="bg-golf-sand/40 rounded-xl p-4">
                        <h3 className="text-sm font-semibold text-golf-gray mb-3">Prospect Summary</h3>
                        <div className="space-y-2 text-sm">
                          <div className="flex justify-between">
                            <span className="text-gray-500">Company</span>
                            <span className="font-medium text-golf-gray">{prospect.company_name}</span>
                          </div>
                          {prospect.industry && (
                            <div className="flex justify-between">
                              <span className="text-gray-500">Industry</span>
                              <span className="text-golf-gray">{prospect.industry}</span>
                            </div>
                          )}
                          {primaryContact && (
                            <div className="flex justify-between">
                              <span className="text-gray-500">Primary Contact</span>
                              <span className="text-golf-gray">{primaryContact.full_name}</span>
                            </div>
                          )}
                          {latestCommitment?.tier_interest && (
                            <div className="flex justify-between">
                              <span className="text-gray-500">Tier Interest</span>
                              <span className="font-medium text-golf-orange">{latestCommitment.tier_interest}</span>
                            </div>
                          )}
                          {latestCommitment?.amount_pledged && (
                            <div className="flex justify-between">
                              <span className="text-gray-500">Pledged</span>
                              <span className="font-medium text-golf-gray">₦{latestCommitment.amount_pledged.toLocaleString()}</span>
                            </div>
                          )}
                        </div>
                      </div>

                      <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-xs text-amber-700">
                        Activating will create a confirmed partner record. This prospect record will be preserved in the pipeline history.
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Internal Notes</label>
                        <textarea {...register('notes')} rows={3} placeholder="Any notes about this activation..." className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-golf-orange/40 focus:border-golf-orange resize-none" />
                      </div>
                    </div>

                    <div className="space-y-4">
                      <h3 className="text-sm font-semibold text-golf-gray">Partner Details</h3>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Tier *</label>
                        <select {...register('tier')} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-golf-orange/40 focus:border-golf-orange bg-white">
                          <option value="">Select tier</option>
                          {tiers.map(t => <option key={t.id} value={t.name}>{t.name}</option>)}
                        </select>
                        {errors.tier && <p className="mt-1 text-xs text-red-600">{errors.tier.message}</p>}
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Contribution (₦) *</label>
                        <input type="number" {...register('contribution', { valueAsNumber: true })} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-golf-orange/40 focus:border-golf-orange" placeholder="0" />
                        {errors.contribution && <p className="mt-1 text-xs text-red-600">{errors.contribution.message}</p>}
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Contact Person *</label>
                        <input {...register('contact_person')} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-golf-orange/40 focus:border-golf-orange" />
                        {errors.contact_person && <p className="mt-1 text-xs text-red-600">{errors.contact_person.message}</p>}
                      </div>

                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
                          <input type="email" {...register('email')} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-golf-orange/40 focus:border-golf-orange" />
                          {errors.email && <p className="mt-1 text-xs text-red-600">{errors.email.message}</p>}
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Phone *</label>
                          <input {...register('phone')} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-golf-orange/40 focus:border-golf-orange" />
                          {errors.phone && <p className="mt-1 text-xs text-red-600">{errors.phone.message}</p>}
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Start Date *</label>
                          <input type="date" {...register('start_date')} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-golf-orange/40 focus:border-golf-orange" />
                          {errors.start_date && <p className="mt-1 text-xs text-red-600">{errors.start_date.message}</p>}
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">End Date *</label>
                          <input type="date" {...register('end_date')} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-golf-orange/40 focus:border-golf-orange" />
                          {errors.end_date && <p className="mt-1 text-xs text-red-600">{errors.end_date.message}</p>}
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Benefits</label>
                        <div className="space-y-1 mb-2 max-h-32 overflow-y-auto">
                          {benefits.map((b, i) => (
                            <div key={i} className="flex items-center gap-2 py-1 px-2 bg-gray-50 rounded-lg">
                              <span className="flex-1 text-xs text-gray-700">{b}</span>
                              <button type="button" onClick={() => removeBenefit(i)} className="text-gray-400 hover:text-red-500 transition-colors">
                                <TrashIcon className="w-3.5 h-3.5" />
                              </button>
                            </div>
                          ))}
                        </div>
                        <div className="flex gap-2">
                          <input value={newBenefit} onChange={e => setNewBenefit(e.target.value)} onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), addBenefit())} className="flex-1 px-3 py-1.5 border border-gray-300 rounded-lg text-xs focus:outline-none focus:ring-2 focus:ring-golf-orange/40 focus:border-golf-orange" placeholder="Add benefit..." />
                          <button type="button" onClick={addBenefit} className="px-2 py-1.5 bg-golf-orange/10 text-golf-orange rounded-lg hover:bg-golf-orange/20 transition-colors">
                            <PlusIcon className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="px-6 py-4 bg-gray-50 border-t border-gray-100 flex justify-end gap-3">
                    <button type="button" onClick={onClose} className="px-4 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-lg hover:bg-white transition-colors">
                      Cancel
                    </button>
                    <button type="submit" disabled={isSubmitting} className="flex items-center gap-2 px-5 py-2 text-sm font-medium text-white bg-golf-orange rounded-lg hover:bg-golf-orange/90 transition-colors disabled:opacity-60">
                      <SparklesIcon className="w-4 h-4" />
                      {isSubmitting ? 'Activating...' : 'Activate as Partner'}
                    </button>
                  </div>
                </form>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}
